import { UNFINISHED_PATH_SOURCE } from './constants';
import { safeSetSourceData } from './utils';
import { globalColorManager } from '../utils/ColorManager';

// Vehicle path renderer - handles both complete and unfinished paths

// Update vehicle paths on map
export const updateVehiclePaths = ({
  map,
  mapReady,
  sourcesReady,
  vehiclePaths,
  locations,
  selectedVehicleIndex = null,
  shouldShowPath = true,
  pathComplete = false,
  forceRefresh = false
}) => {
  if (!map || !mapReady || !sourcesReady) {
    console.log('[VehiclePathRenderer] Map not ready, skipping update');
    return;
  }

  // If no need to show paths or no path data, clear display
  if (!shouldShowPath || !vehiclePaths || vehiclePaths.length === 0) {
    const emptyData = {
      type: 'FeatureCollection',
      features: [],
      properties: { 
        pathType: pathComplete ? 'completed' : 'unfinished',
        vehicle_count: 0
      }
    };
    
    safeSetSourceData(map, mapReady, sourcesReady, UNFINISHED_PATH_SOURCE, emptyData);
    console.log('[VehiclePathRenderer] Cleared vehicle paths');
    return;
  }

  try {
    const features = [];
    let totalProcessedVehicles = 0;

    // Process each vehicle's path
    vehiclePaths.forEach((vehicle, vehicleIndex) => {
      // Skip vehicles without paths or with empty paths
      if (!vehicle.path || vehicle.path.length === 0) {
        return;
      }

      // Build coordinate array for vehicle path
      const vehicleCoordinates = vehicle.path
        .map(point => {
          // Find corresponding location point
          const location = locations.find(loc => 
            loc.id === point.id || 
            loc.id === point || 
            loc === point
          );

          if (location && location.longitude && location.latitude) {
            return [location.longitude, location.latitude];
          }

          // If point itself contains coordinate information
          if (point.longitude && point.latitude) {
            return [point.longitude, point.latitude];
          }

          return null;
        })
        .filter(Boolean); // Remove null values

      // Only add when path contains at least one valid coordinate
      if (vehicleCoordinates.length >= 1) {
        const isSelected = vehicleIndex === selectedVehicleIndex;
        const vehicleId = vehicle.id || vehicle.vehicle_id || vehicleIndex + 1;
        
        // Get vehicle color
        const vehicleColor = vehicle.color || 
                           globalColorManager.getVehicleColor(vehicleId, vehicle);

        // Create GeoJSON Feature for vehicle path
        const pathFeature = {
          type: 'Feature',
          properties: {
            // Basic properties
            pathType: pathComplete ? 'completed' : 'unfinished',
            vehicle_id: vehicleId,
            vehicle_index: vehicleIndex,
            
            // Visual properties
            color: vehicleColor,
            
            // State properties
            isCompleted: pathComplete,
            isSelected: !pathComplete && isSelected, // Only highlight selected in unfinished state
            isBeingEdited: !pathComplete && isSelected,
            
            // Path information
            path_length: vehicle.path.length,
            coordinate_count: vehicleCoordinates.length,
            
            // Distance and capacity information (if available)
            distance: vehicle.distance || 0,
            capacity: vehicle.capacity || null,
            load: vehicle.load || null,
            
            // Dynamic line style properties
            lineWidth: (() => {
              if (pathComplete) {
                return 4; // Completed paths use standard width
              } else {
                return isSelected ? 5 : 3; // Unfinished paths: thicker when selected
              }
            })(),
            
            lineOpacity: (() => {
              if (pathComplete) {
                return 0.85; // Completed paths standard opacity
              } else {
                return isSelected ? 0.9 : 0.7; // Unfinished paths: more opaque when selected
              }
            })()
          },
          geometry: {
            type: 'LineString',
            coordinates: vehicleCoordinates
          }
        };

        features.push(pathFeature);
        totalProcessedVehicles++;
      }
    });

    // Build final GeoJSON data
    const pathData = {
      type: 'FeatureCollection',
      features: features,
      properties: {
        // Data type
        pathType: pathComplete ? 'completed' : 'unfinished',
        data_source: 'VehiclePathRenderer',
        
        // State information
        is_completed: pathComplete,
        is_building: !pathComplete,
        
        // Statistics information
        vehicle_count: totalProcessedVehicles,
        total_vehicles: vehiclePaths.length,
        selected_vehicle_index: selectedVehicleIndex,
        
        // Metadata
        timestamp: Date.now(),
        force_refresh: forceRefresh
      }
    };

    // Update map data source
    safeSetSourceData(map, mapReady, sourcesReady, UNFINISHED_PATH_SOURCE, pathData);
    
    console.log(`[VehiclePathRenderer] Updated ${totalProcessedVehicles} vehicle paths`, {
      pathComplete,
      selectedVehicleIndex,
      totalFeatures: features.length
    });

  } catch (error) {
    console.error('[VehiclePathRenderer] Error updating vehicle paths:', error);
    
    // Clear path display on error
    const errorData = {
      type: 'FeatureCollection',
      features: [],
      properties: { 
        pathType: 'error',
        error_message: error.message,
        timestamp: Date.now()
      }
    };
    
    safeSetSourceData(map, mapReady, sourcesReady, UNFINISHED_PATH_SOURCE, errorData);
  }
};

/**
 * Clear vehicle path display
 */
export const clearVehiclePaths = (map, mapReady, sourcesReady) => {
  if (!map || !mapReady || !sourcesReady) return;
  
  const emptyData = {
    type: 'FeatureCollection',
    features: [],
    properties: { 
      pathType: 'cleared',
      timestamp: Date.now()
    }
  };
  
  safeSetSourceData(map, mapReady, sourcesReady, UNFINISHED_PATH_SOURCE, emptyData);
  console.log('[VehiclePathRenderer] Cleared all vehicle paths');
};

/**
 * Get vehicle path statistics information
 */
export const getVehiclePathStats = (vehiclePaths) => {
  if (!vehiclePaths || vehiclePaths.length === 0) {
    return {
      totalVehicles: 0,
      totalDistance: 0,
      totalStops: 0,
      averageStopsPerVehicle: 0
    };
  }

  const stats = vehiclePaths.reduce((acc, vehicle) => {
    if (vehicle.path && vehicle.path.length > 0) {
      acc.totalDistance += vehicle.distance || 0;
      acc.totalStops += vehicle.path.length;
    }
    return acc;
  }, {
    totalVehicles: vehiclePaths.length,
    totalDistance: 0,
    totalStops: 0
  });

  stats.averageStopsPerVehicle = stats.totalStops / stats.totalVehicles;

  return stats;
};
